<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import CreateIntegrationSidePanel from '@/components/SidePanels/Integrations/CreateIntegration.vue'
import { integrationsModule } from '@/store/integrations/module'
import { toastModule } from '@/store/toast/module'
import { ref } from 'vue'

const {
    ModalContainer,
    Tag,
    Button: PocketButton,
    Link: PocketLink,
} = UIKit

const props = withDefaults(defineProps<{
    integrationActive?: boolean
    integrationTitle?: string
    integrationId?: string
}>(), {
    integrationActive: false,
    integrationTitle: 'Canvas LMS',
    integrationId: undefined,
})

const isLoading = ref(false)

const showCreateIntegrationSidePanel = ref(false)
const showDeleteIntegrationModal = ref(false)

const createIntegration = () => {
    showCreateIntegrationSidePanel.value = true
}

const deleteIntegration = async () => {
    if (!props.integrationId) {
        return
    }
    isLoading.value = true
    await integrationsModule.actions.deleteLMSIntegration({
        lmsId: props.integrationId,
    })

    toastModule.actions.displayToast({
        title: 'Canvas LMS deleted.',
    })

    await integrationsModule.actions.fetchLMSIntegrations(true)

    isLoading.value = false
    showDeleteIntegrationModal.value = false
}
</script>

<template>
    <div
        class="integration-card">
        <CreateIntegrationSidePanel
            v-if="showCreateIntegrationSidePanel"
            @close="showCreateIntegrationSidePanel = false"
        />
        <Portal to="modal">
            <ModalContainer
                v-if="showDeleteIntegrationModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="showDeleteIntegrationModal = false">
                <template #head>
                    <div
                        class="integration-card__delete-integration-head"
                    >
                        Delete Integration?
                    </div>
                </template>
                <div
                    class="integration-card__delete-modal"
                >
                    <span
                        class="integration-card__delete-text"
                    >
                        Students and instructors will no longer be able to access Pocket Prep directly from Canvas.
                        The courses you integrated will be deactivated and removed.
                    </span>
                    <div
                        class="integration-card__delete-buttons"
                    >
                        <PocketButton
                            class="integration-card__nevermind-btn"
                            type="secondary"
                            @click="showDeleteIntegrationModal = false"
                        >
                            Never mind
                        </PocketButton>
                        <PocketButton
                            class="integration-card__cancel-btn"
                            type="primary-red"
                            @click="deleteIntegration"
                        >
                            Delete
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
        <Tag
            class="integration-card__tag"
            :class="{
                'integration-card__tag--inactive': !integrationActive
            }"
            :is-archived-tag="!integrationActive"
            :is-active-tag="integrationActive"
        >
            <template #tagMessage>
                {{ integrationActive ? 'Active' : 'Inactive' }}
            </template>
        </Tag>
        <img
            class="integration-card__canvas-logo"
            src="@/assets/images/canvas.svg"
            alt="Canvas logo"
        >
        <div
            class="integration-card__text-container"
        >
            <div class="integration-card__title">
                {{ integrationTitle }}
            </div>
            <div class="integration-card__description">
                Invite students, assign instructors to students, and assign to Dashboard groups in a single click.
            </div>
            <PocketLink
                class="integration-card__link"
                target="_blank"
                href="https://help.educators.pocketprep.com/en/articles/9953670-introducing-pocket-prep-for-canvas-lms"
            >
                Learn how the integration works
            </PocketLink>
        </div>
        <PocketButton
            v-if="integrationActive"
            class="integration-card__button"
            type="primary-red"
            @click="showDeleteIntegrationModal = true"
        >
            Delete
        </PocketButton>
        <PocketButton
            v-else
            class="integration-card__button"
            type="primary"
            @click="createIntegration"
        >
            Connect
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>


.integration-card {
    width: 384px;
    height: 249px;
    border: 0.5px solid rgba($pewter, 0.85);
    border-radius: 5px;
    position: relative;

    &__tag {
        margin: 24px 0 20px 21px;
    }

    &__canvas-logo {
        position: absolute;
        width: 38px;
        height: 38px;
        right: 24px;
        top: 20px;
    }

    &__text-container {
        margin-left: 21px;
    }

    &__title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 5px;
    }

    &__description {
        width: 336px;
        font-size: 15px;
        margin-bottom: 15px;
        line-height: 20px;
    }

    &__button {
        position: absolute;
        right: 24px;
        bottom: 24px;
    }


    &__delete-integration-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__delete-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__nevermind-btn {
        margin-right: 18px;
    }

    &__delete-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__delete-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }
}
</style>
